import { Button, Typography } from "@povio/ui";
import { useTranslation } from "react-i18next";

import { DotsVerticalIcon } from "@/assets/icons/general/DotsVertical";
import { ModalSheet } from "@/components/shared/modal/ModalSheet";

interface AndroidInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Android PWA Installation Instructions Modal
 * Shows step-by-step instructions for installing the app on Android devices
 * Used when the beforeinstallprompt API is not available
 */
export const AndroidInstallModal = ({ isOpen, onClose }: AndroidInstallModalProps) => {
  const { t } = useTranslation();

  return (
    <ModalSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t("shared.pwa.android.modalTitle")}
      fitContent
    >
      <div className="flex flex-col gap-6">
        {/* Introduction */}
        <Typography
          variant="default"
          size="body-4"
          className="text-content-default-1"
        >
          {t("shared.pwa.android.intro")}
        </Typography>

        {/* Step-by-step instructions */}
        <div className="flex flex-col gap-4">
          {/* Step 1 */}
          <div className="flex flex-row gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-interactive-contained-primary-idle">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-white"
              >
                1
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-content-default-1"
              >
                {t("shared.pwa.android.step1Title")}
              </Typography>
              <Typography
                variant="default"
                size="body-4"
                className="text-content-default-2"
              >
                {t("shared.pwa.android.step1Description")}
              </Typography>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-row gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-interactive-contained-primary-idle">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-white"
              >
                2
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-content-default-1"
              >
                {t("shared.pwa.android.step2Title")}
              </Typography>
              <Typography
                variant="default"
                size="body-4"
                className="text-content-default-2"
              >
                {t("shared.pwa.android.step2Description")}
              </Typography>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-row gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-interactive-contained-primary-idle">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-white"
              >
                3
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-content-default-1"
              >
                {t("shared.pwa.android.step3Title")}
              </Typography>
              <Typography
                variant="default"
                size="body-4"
                className="text-content-default-2"
              >
                {t("shared.pwa.android.step3Description")}
              </Typography>
            </div>
          </div>
        </div>

        {/* Visual hint with menu icon */}
        <div className="flex flex-col items-center gap-2 rounded-lg bg-elevation-fill-default-1 p-4">
          <Typography
            variant="prominent-1"
            size="body-4"
            className="text-content-default-2"
          >
            {t("shared.pwa.android.lookFor")}
          </Typography>
          <div className="flex items-center justify-center rounded-lg bg-interactive-contained-primary-idle p-3">
            <DotsVerticalIcon className="h-6 w-6 text-white" />
          </div>
          <Typography
            variant="default"
            size="body-4"
            className="text-center text-content-default-3"
          >
            {t("shared.pwa.android.menuHint")}
          </Typography>
        </div>

        {/* Close button */}
        <Button
          variant="contained"
          color="primary"
          size="m"
          width="fill"
          onPress={onClose}
        >
          {t("shared.pwa.android.gotItButton")}
        </Button>
      </div>
    </ModalSheet>
  );
};
